
/* ============================== src/components/Kanban.jsx ============================== */
/**
 * Канбан. Добавлена поддержка prop totals для отображения реального количества задач в колонках.
 */
import { useState, useMemo, memo, useRef, useLayoutEffect } from "react";
import { Ic } from "./Icons";
import { ICONS } from "./iconsData";
import { SearchBox } from "./SearchBox";
import { ColorDot } from "./ColoredSpan";

const KanbanColumn = memo(function KanbanColumn({
  status,
  label,
  color,
  items,
  total = 0,
  renderCard,
  onDrop,
  onDragStateChange,
  isDragOver,
  emptyMessage,
}) {
  const handleDragOver = (e) => {
    e.preventDefault();
    if (!isDragOver) onDragStateChange(status);
  };
  const handleDragLeave = () => {
    if (isDragOver) onDragStateChange(null);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    onDragStateChange(null);
    const id = e.dataTransfer.getData("text/plain");
    if (id) onDrop(id, status);
  };

  return (
    <div
      className={`kcol${isDragOver ? " over" : ""}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className="kcol-head">
        <ColorDot color={color} className="kdot" />
        {label}
        <span className="kcount">{total || items.length}</span>
      </div>
      <div className="kcol-body">
        {items.length === 0 ? (
          <div className="kempty">{emptyMessage}</div>
        ) : (
          items.map((item) => (
            <div
              key={item.id}
              className="kcard"
              draggable
              onDragStart={(e) => e.dataTransfer.setData("text/plain", item.id)}
            >
              {renderCard(item)}
            </div>
          ))
        )}
      </div>
    </div>
  );
});

function Kanban({
  items,
  totals = {},
  statusOrder,
  statusMap,
  renderCard,
  onDrop,
  onNew,
  searchQuery,
  onSearchChange,
  showOnlyMy,
  onToggleMy,
  extraFilters,
  emptyMessage = "Нет элементов",
  columns,
}) {
  const [dragOverCol, setDragOverCol] = useState(null);

  const byStatus = useMemo(() => {
    const m = new Map();
    statusOrder.forEach((st) => m.set(st, []));
    items.forEach((item) => {
      const list = m.get(item.status);
      if (list) list.push(item);
    });
    return m;
  }, [items, statusOrder]);

  const kanbanClasses = ["kanban"];
  if (!columns && statusOrder.length === 5) kanbanClasses.push("k5");

  const kanbanRef = useRef(null);
  useLayoutEffect(() => {
    kanbanRef.current?.style.setProperty("--columns", columns ?? "");
  }, [columns]);

  return (
    <div>
      <div className="toolbar">
        {onSearchChange && (
          <SearchBox
            value={searchQuery}
            onChange={onSearchChange}
            placeholder="Поиск…"
          />
        )}
        {extraFilters}
        {onToggleMy && (
          <label className="dept-pick ml-2">
            <input
              type="checkbox"
              checked={showOnlyMy}
              onChange={(e) => onToggleMy(e.target.checked)}
            />
            <span className="text-sm">Только мои</span>
          </label>
        )}
        {onNew && (
          <button className="btn primary" onClick={onNew}>
            <Ic d={ICONS.plus} size={15} /> Создать
          </button>
        )}
      </div>

      <div ref={kanbanRef} className={kanbanClasses.join(" ")}>
        {statusOrder.map((st) => {
          const meta = statusMap[st];
          return (
            <KanbanColumn
              key={st}
              status={st}
              label={meta.label}
              color={meta.color}
              items={byStatus.get(st) || []}
              total={totals[st]}
              renderCard={renderCard}
              onDrop={onDrop}
              onDragStateChange={setDragOverCol}
              isDragOver={dragOverCol === st}
              emptyMessage={emptyMessage}
            />
          );
        })}
      </div>
    </div>
  );
}

export default memo(Kanban);
